import { InvoiceViewer } from "@/components/shared/file-viewers/invoice-viewer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatAmount } from "@/lib/utils";
import { useGetAccountPeriodEntries } from "@/queries/ledger/accounts/get-period-entries";
import { useNavigate, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import { Calendar, ExternalLink, Eye, FileText, X } from "lucide-react";
import { useMemo, useState } from "react";

interface AccountPeriodSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  periodStartDate: Date | string | null;
  periodEndDate: Date | string | null;
  periodLabel: string | null;
}

export function AccountPeriodSheet({
  open,
  onOpenChange,
  accountId,
  accountNumber,
  accountName,
  periodStartDate,
  periodEndDate,
  periodLabel,
}: AccountPeriodSheetProps) {
  const navigate = useNavigate();
  const { company } = useParams({ strict: false });

  const [invoicePreview, setInvoicePreview] = useState<{
    open: boolean;
    invoiceId: string | null;
    invoiceNo: string | null;
  }>({
    open: false,
    invoiceId: null,
    invoiceNo: null,
  });

  const {
    data: entries,
    loading,
    error,
  } = useGetAccountPeriodEntries({
    accountId,
    startDate: periodStartDate,
    endDate: periodEndDate,
  });

  // Group entries by invoice and calculate totals
  const { invoiceEntries, totals } = useMemo(() => {
    if (!entries || entries.length === 0) {
      return {
        invoiceEntries: [],
        totals: { debit: 0, credit: 0, balance: 0 },
      };
    }

    const safeNumber = (value: number | null | undefined): number => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    // Map to store invoice data (group multiple entries for same invoice)
    const invoiceMap = new Map<
      string,
      {
        invoice: (typeof entries)[0]["invoice"];
        firstEntry: (typeof entries)[0];
        debit: number;
        credit: number;
        balance: number;
        date: Date;
        counterpartyName: string | null;
        journalEntryCount: number;
      }
    >();

    let totalDebit = 0;
    let totalCredit = 0;

    for (const entry of entries) {
      totalDebit += safeNumber(entry.debit);
      totalCredit += safeNumber(entry.credit);

      const counterpartyName = entry.counterparty?.entityName || null;
      const entryDate = new Date(entry.timestamp);

      // If there's an invoice, group by it
      if (entry.invoice) {
        const invoiceId = entry.invoice.invoiceId;
        const existing = invoiceMap.get(invoiceId);

        if (existing) {
          // Accumulate amounts for this invoice across multiple entries (line items)
          existing.debit += safeNumber(entry.debit);
          existing.credit += safeNumber(entry.credit);
          existing.balance = existing.debit - existing.credit;
          existing.journalEntryCount += 1;
          // Update counterparty name if we didn't have one before
          if (!existing.counterpartyName && counterpartyName) {
            existing.counterpartyName = counterpartyName;
          }
        } else {
          const debit = safeNumber(entry.debit);
          const credit = safeNumber(entry.credit);
          invoiceMap.set(invoiceId, {
            invoice: entry.invoice,
            firstEntry: entry,
            debit,
            credit,
            balance: debit - credit,
            date: entryDate,
            counterpartyName,
            journalEntryCount: 1,
          });
        }
      } else {
        // Entries without invoices
        const debit = safeNumber(entry.debit);
        const credit = safeNumber(entry.credit);
        invoiceMap.set(entry.journalEntryId, {
          invoice: null,
          firstEntry: entry,
          debit,
          credit,
          balance: debit - credit,
          date: entryDate,
          counterpartyName,
          journalEntryCount: 1,
        });
      }
    }

    const invoiceEntries = Array.from(invoiceMap.values()).sort(
      (a, b) => b.date.getTime() - a.date.getTime(),
    );

    const balance = totalDebit - totalCredit;

    return {
      invoiceEntries,
      totals: {
        debit: totalDebit,
        credit: totalCredit,
        balance,
      },
    };
  }, [entries]);

  const handleInvoiceClick = (
    invoiceId: string,
    invoiceTitle: string | null,
  ) => {
    setInvoicePreview({
      open: true,
      invoiceId,
      invoiceNo: invoiceTitle,
    });
  };

  const handleNavigateToInvoice = (invoiceId: string) => {
    if (!company) return;
    navigate({
      to: "/$company/unternehmen/rechnung/$id",
      params: { company, id: invoiceId },
    });
  };

  return (
    <>
      {invoicePreview.open && invoicePreview.invoiceId && (
        <div className="fixed inset-0 right-[600px] bg-background border-r z-40 overflow-hidden">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-background">
              <h2 className="text-lg font-semibold">
                {invoicePreview.invoiceNo || "Rechnungsvorschau"}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setInvoicePreview({
                    open: false,
                    invoiceId: null,
                    invoiceNo: null,
                  })
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <InvoiceViewer
                invoiceId={invoicePreview.invoiceId}
                toolbar={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sheet */}
      <div
        className={`fixed top-0 right-0 h-full w-[600px] bg-background border-l shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b p-6 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-semibold">
                  {accountNumber} - {accountName}
                </h2>
                {periodLabel && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-2">
                    <Calendar className="h-4 w-4" />
                    <span>{periodLabel}</span>
                    {periodStartDate && periodEndDate && (
                      <span className="text-xs">
                        ({format(periodStartDate, "dd.MM.yyyy")} -{" "}
                        {format(periodEndDate, "dd.MM.yyyy")})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Soll
                </span>
                <span className="font-mono font-semibold">
                  {formatAmount(totals.debit, "EUR")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Haben
                </span>
                <span className="font-mono font-semibold">
                  {formatAmount(totals.credit, "EUR")}
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold">Saldo</span>
                <span
                  className={`font-mono text-lg font-bold ${
                    totals.balance < 0
                      ? "text-red-600"
                      : totals.balance > 0
                        ? "text-green-600"
                        : "text-muted-foreground"
                  }`}
                >
                  {formatAmount(totals.balance, "EUR")}
                </span>
              </div>
            </div>

            {loading && (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <p className="text-sm text-destructive">
                  Fehler beim Laden der Buchungen. Bitte versuchen Sie es
                  erneut.
                </p>
              </div>
            )}

            {/* Invoices */}
            {!loading && !error && invoiceEntries.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">
                    Belege ({invoiceEntries.length})
                  </h3>
                </div>

                <div className="space-y-2">
                  {invoiceEntries.map((item) => {
                    const invoice = item.invoice;

                    return (
                      <div
                        key={
                          invoice?.invoiceId || item.firstEntry.journalEntryId
                        }
                        className={`border rounded-lg p-4 transition-all space-y-2 ${
                          invoice ? "hover:bg-muted/50" : "bg-muted/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <FileText
                              className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                                invoice
                                  ? "text-primary"
                                  : "text-muted-foreground"
                              }`}
                            />
                            <div className="flex-1 min-w-0 space-y-1">
                              {invoice ? (
                                <>
                                  <div className="font-medium truncate flex items-center gap-2">
                                    {item.counterpartyName || "Unbekannt"}
                                    {item.journalEntryCount > 1 && (
                                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                        {item.journalEntryCount} Positionen
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {invoice.aiSummary || "Keine Beschreibung"}
                                    {invoice.invoiceDate && (
                                      <span className="ml-2">
                                        •{" "}
                                        {format(
                                          new Date(invoice.invoiceDate),
                                          "dd.MM.yyyy",
                                        )}
                                      </span>
                                    )}
                                    {invoice.invoiceNo && (
                                      <span className="ml-2">
                                        • #{invoice.invoiceNo}
                                      </span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="font-medium">
                                    {item.firstEntry.bookingText || "Buchung"}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {format(item.date, "dd.MM.yyyy HH:mm")}
                                  </div>
                                  {item.counterpartyName && (
                                    <div className="text-sm text-muted-foreground">
                                      {item.counterpartyName}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Balance Display */}
                            <div className="text-right">
                              <div
                                className={`text-base font-semibold ${
                                  item.balance < 0
                                    ? "text-red-600"
                                    : item.balance > 0
                                      ? "text-green-600"
                                      : ""
                                }`}
                              >
                                {formatAmount(item.balance, "EUR")}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            {invoice && (
                              <div className="flex items-center gap-1.5">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleInvoiceClick(
                                            invoice.invoiceId,
                                            invoice.aiSummary || null,
                                          );
                                        }}
                                      >
                                        <Eye className="h-3 w-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Dokument ansehen
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNavigateToInvoice(
                                            invoice.invoiceId,
                                          );
                                        }}
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Detailseite öffnen
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && !error && invoiceEntries.length === 0 && (
              <div className="flex flex-col items-center justify-center h-[300px] gap-4 text-muted-foreground">
                <FileText className="h-16 w-16 opacity-20" />
                <div className="text-center">
                  <h3 className="text-lg font-semibold mb-2 text-foreground">
                    Keine Belege
                  </h3>
                  <p className="text-sm max-w-md">
                    Für dieses Konto und diese Periode wurden keine Rechnungen
                    oder Belege gefunden.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
